import { columns as allAflColumns } from './columns';
import { applyColumnFiltersToParams } from './filterUtils';
// Mapping functions for converting UI state to API parameters and CSV export

export interface ApiParams {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}

export interface CsvParams {
  startDate?: string;
  endDate?: string;
  filters?: Record<string, any>;
  columns?: string[];
}

export function mapStateToParams(
  date: { startDate: string; endDate: string },
  pagination: { pageIndex: number; pageSize: number },
  sorting: Array<{ id: string; desc: boolean }>,
  globalFilter: string,
): URLSearchParams {
  const p = new URLSearchParams();

  // Only set date params if they have valid values (not undefined, null, or empty string)
  if (date.startDate && date.startDate !== 'undefined' && date.startDate !== 'null') {
    p.set('startDate', date.startDate);
  }
  if (date.endDate && date.endDate !== 'undefined' && date.endDate !== 'null') {
    p.set('endDate', date.endDate);
  }
  p.set('page', String(pagination.pageIndex + 1));
  p.set('pageSize', String(pagination.pageSize));

  if (globalFilter) {
    p.set('search', globalFilter);
  }

  if (sorting.length > 0) {
    const sort = sorting[0];
    p.set('sortBy', sort.id);
    p.set('sortDir', sort.desc ? 'desc' : 'asc');
  }

  return p;
}

export function mapCsvParams(
  date: { startDate: string; endDate: string },
  columnFilters: Array<{ id: string; value: any }>,
  columnVisibility: Record<string, boolean>,
  options?: {
    columnOrder?: string[];
    sorting?: Array<{ id: string; desc: boolean }>;
    globalFilter?: string;
  },
): URLSearchParams {
  const p = new URLSearchParams();

  // Date range - only set if valid values (not undefined, null, or empty string)
  if (date.startDate && date.startDate !== 'undefined' && date.startDate !== 'null') {
    p.set('startDate', date.startDate);
  }
  if (date.endDate && date.endDate !== 'undefined' && date.endDate !== 'null') {
    p.set('endDate', date.endDate);
  }

  // Sorting from current table state (use the first sort only, same as API/UI behavior)
  if (options?.sorting && options.sorting.length > 0) {
    const s = options.sorting[0];
    p.set('sortBy', s.id);
    p.set('sortDir', s.desc ? 'desc' : 'asc');
  }

  // Global search
  if (options?.globalFilter) {
    p.set('search', options.globalFilter);
  }

  applyColumnFiltersToParams(p, columnFilters as any);

  // Ordered visible columns
  // Build ordered visible columns using provided order or default AFL columns.
  const defaultOrder: string[] = (allAflColumns as any[])
    .map((c: any) => c?.accessorKey || c?.id || c?.header)
    .filter((v: any) => typeof v === 'string');
  const baseOrder =
    options?.columnOrder && options.columnOrder.length > 0 ? options.columnOrder : defaultOrder;
  // Drop columns that are explicitly hidden; keep others by default.
  const orderedVisible: string[] = baseOrder.filter((col) => columnVisibility[col] !== false);
  if (orderedVisible.length > 0) {
    p.set('columns', orderedVisible.join(','));
  }

  return p;
}
